// Sites: what the map shows for World Heritage sites and resource deposits, decided from records
// alone (no three.js), so the rules are unit-testable and every marker keeps its source ids.
//
//   snapshot.wonders ─► inscribed by the date ─► one per hex (the bake's wonder-field rule)
//                                             ─► the rest of the hex counted, never drawn apart
//   snapshot.deposits ─► one per (hex, resource kind); no quantities, so nothing reads as a statistic
//
// Truth: a marker exists only where a record does. A group is "sourced" only when every record in
// it carries a source id; `unsourced()` in install.ts counts the groups that are not.

import { hexDistance, hexRow } from '../../core/grid.ts';
import { RESOURCE, type Deposit, type SourceId, type Wonder } from '../../core/schema.ts';

export type WonderCategory = Wonder['category'];

/** Same order as the bake's wonder field (tools/bake/worldciv_bake/sites_wonders.py). */
const CATEGORY_RANK: Readonly<Record<WonderCategory, number>> = { cultural: 0, mixed: 1, natural: 2 };

const YEAR_DIGITS = 4;
const FNV_OFFSET = 2166136261;
const FNV_PRIME = 16777619;

/** A site of the hex that is on the danger list at the date, and since when. */
export interface DangerNote {
  name: string;
  /** First year of the run of danger spans that holds the date's year. */
  since: number;
}

/** One drawn wonder: the hex's primary site, with the other sites of the hex counted. */
export interface WonderSite {
  /** Marker id: `w:<Wonder.id of the primary>`. */
  key: string;
  hex: number;
  name: string;
  category: WonderCategory;
  /** Names of every site in the hex, primary first. */
  names: string[];
  /** Other sites in the same hex (the count badge). */
  extra: number;
  /** True when any site of the hex is on the danger list at the date. */
  danger: boolean;
  /** Wonder.id of each site in danger at the date. */
  dangerIds: number[];
  /** Name and start year of each site in danger at the date. */
  dangers: DangerNote[];
  /** True when a site of the hex is the first inscribed of one of its countries (a fact, not a rank). */
  lead: boolean;
  /** Union of the group's sources. */
  sources: SourceId[];
  /** Every record of the group carries at least one source. */
  sourced: boolean;
  /** Stable neutral order for declutter (a hash, not an importance judgement). */
  rank: number;
}

/** One drawn deposit: a resource kind in a hex. */
export interface ResourceSite {
  /** Marker id: `d:<hex>:<kind>`. */
  key: string;
  hex: number;
  /** RESOURCE value. */
  kind: number;
  names: string[];
  sources: SourceId[];
  sourced: boolean;
  rank: number;
}

/** Year of a 'YYYY-MM-DD' date. */
export function yearOf(date: string): number {
  return Number(date.slice(0, YEAR_DIGITS));
}

/** Whether the site is on the danger list in `year`; both ends of a span are inclusive. */
export function inDanger(w: Pick<Wonder, 'danger'>, year: number): boolean {
  return w.danger.some((span) => span.from <= year && (span.to === null || year <= span.to));
}

/**
 * First year of the danger run that holds `year`: spans that touch (one ends the year before the next
 * begins) count as one run. Null when the site is not in danger that year.
 */
export function dangerSince(w: Pick<Wonder, 'danger'>, year: number): number | null {
  const spans = [...w.danger].sort((a, b) => a.from - b.from);
  const at = spans.findIndex((span) => span.from <= year && (span.to === null || year <= span.to));
  if (at < 0) {
    return null;
  }

  let since = spans[at].from;
  for (let i = at - 1; i >= 0; i--) {
    const to = spans[i].to;
    if (to === null || to + 1 < since) {
      break;
    }
    since = Math.min(since, spans[i].from);
  }
  return since;
}

const DANGER_SINCE = 'On UNESCO danger list since';
const MORE_NAMES_SHOWN = 3;

/** Hover rows for a danger-listed site of a hex whose chip already shows `shown` as its name. */
export function dangerRows(shown: string, note: DangerNote): string[] {
  const since = `${DANGER_SINCE} ${note.since}`;
  return note.name === shown ? [since] : [note.name, since];
}

/** The other sites of a hex (names[0] is the chip's own): the first few, then a count of the rest. */
export function moreNames(names: readonly string[], shown: number = MORE_NAMES_SHOWN): string[] {
  const others = names.slice(1);
  if (others.length <= shown) {
    return others;
  }
  return [...others.slice(0, shown), `+${others.length - shown} more`];
}

/** FNV-1a of a string, 0..2^32. */
export function hashKey(s: string): number {
  let h = FNV_OFFSET;
  for (let i = 0; i < s.length; i++) {
    h = Math.imul(h ^ s.charCodeAt(i), FNV_PRIME);
  }
  return h >>> 0;
}

function unionOf(lists: ReadonlyArray<readonly SourceId[]>): SourceId[] {
  return [...new Set(lists.flat())];
}

/** Order of the bake's wonder field: earliest inscription, then cultural < mixed < natural, then id. */
function byField(a: Wonder, b: Wonder): number {
  return a.year - b.year || CATEGORY_RANK[a.category] - CATEGORY_RANK[b.category] || a.id - b.id;
}

/** Wonders inscribed by `date`, one drawn site per hex. */
export function pickWonders(wonders: readonly Wonder[], date: string): WonderSite[] {
  const year = yearOf(date);
  const byHex = new Map<number, Wonder[]>();
  for (const w of wonders) {
    // A site inscribed after the date does not exist yet.
    if (w.year > year) {
      continue;
    }

    const group = byHex.get(w.hex);
    if (group) {
      group.push(w);
      continue;
    }
    byHex.set(w.hex, [w]);
  }

  // The first inscribed site of each country (the bake's field order), among those that exist.
  const first = new Map<string, number>();
  const inscribed = [...byHex.values()].flat().sort(byField);
  for (const w of inscribed) {
    for (const iso of w.iso3s) {
      if (!first.has(iso)) {
        first.set(iso, w.id);
      }
    }
  }

  const sites: WonderSite[] = [];
  for (const [hex, group] of byHex) {
    group.sort(byField);
    const primary = group[0];
    const key = `w:${primary.id}`;
    const inTrouble = group.filter((w) => inDanger(w, year));
    sites.push({
      key,
      hex,
      name: primary.name,
      category: primary.category,
      names: group.map((w) => w.name),
      extra: group.length - 1,
      danger: inTrouble.length > 0,
      dangerIds: inTrouble.map((w) => w.id),
      dangers: inTrouble.map((w) => ({ name: w.name, since: dangerSince(w, year) ?? year })),
      lead: group.some((w) => w.iso3s.some((iso) => first.get(iso) === w.id)),
      sources: unionOf(group.map((w) => w.sources)),
      sourced: group.every((w) => w.sources.length > 0),
      rank: hashKey(key),
    });
  }
  return sites.sort((a, b) => a.hex - b.hex);
}

/** RESOURCE values the map draws (1..12). */
export function isKnownKind(kind: number): boolean {
  return Number.isInteger(kind) && kind > RESOURCE.NONE && kind <= RESOURCE.GRAPHITE;
}

/** Deposits grouped by (hex, kind); a record with an unknown kind is skipped, never guessed. */
export function groupDeposits(deposits: readonly Deposit[]): ResourceSite[] {
  const groups = new Map<string, Deposit[]>();
  for (const d of deposits) {
    if (!isKnownKind(d.kind)) {
      continue;
    }

    const key = `d:${d.hex}:${d.kind}`;
    const group = groups.get(key);
    if (group) {
      group.push(d);
      continue;
    }
    groups.set(key, [d]);
  }

  const sites: ResourceSite[] = [];
  for (const [key, group] of groups) {
    sites.push({
      key,
      hex: group[0].hex,
      kind: group[0].kind,
      names: group.flatMap((d) => (d.name ? [d.name] : [])),
      sources: unionOf(group.map((d) => d.sources)),
      sourced: group.every((d) => d.sources.length > 0),
      rank: hashKey(key),
    });
  }
  return sites.sort((a, b) => a.hex - b.hex || a.kind - b.kind);
}

/** Sites of one hex together, hexes in ascending order. */
export function clusterByHex<T extends { hex: number }>(sites: readonly T[]): Map<number, T[]> {
  const out = new Map<number, T[]>();
  for (const s of sites) {
    const list = out.get(s.hex);
    if (list) {
      list.push(s);
      continue;
    }
    out.set(s.hex, [s]);
  }
  return out;
}

// ─── Zoom bands ────────────────────────────────────────────────────────────────────────────────

/** Camera heights (hex radii): fully shown below `full`, gone above `gone`. */
export interface Band {
  full: number;
  gone: number;
}

export const BANDS = {
  /** Every wonder's chip. */
  wonderChip: { full: 45, gone: 60 },
  /** Farther out only the chips of `lead` and danger sites stay. */
  wonderChipFar: { full: 110, gone: 150 },
  wonderModel: { full: 42, gone: 52 },
  resourceChip: { full: 45, gone: 60 },
  resourceProp: { full: 14, gone: 18 },
  lensProp: { full: 36, gone: 46 },
} as const satisfies Record<string, Band>;

/** 1 below `full`, 0 above `gone`, linear between. */
export function bandAlpha(height: number, band: Band): number {
  if (height <= band.full) {
    return 1;
  }
  if (height >= band.gone) {
    return 0;
  }
  return (band.gone - height) / (band.gone - band.full);
}

/** How visible a wonder's chip is: all of them close in, the lead and danger ones far out too. */
export function wonderChipAlpha(site: Pick<WonderSite, 'lead' | 'danger'>, height: number): number {
  const near = bandAlpha(height, BANDS.wonderChip);
  if (!site.lead && !site.danger) {
    return near;
  }
  return Math.max(near, bandAlpha(height, BANDS.wonderChipFar));
}

/** A shift on the ground from a hex centre, in world units. */
export interface Offset {
  x: number;
  z: number;
}

/** How resources are shown: under the resources lens, or as they are at rest. */
export type ResourceView = 'lens' | 'plain';

/** The band resource chips use: the resources lens shows them from any height. */
export function resourceChipAlpha(height: number, view: ResourceView): number {
  return view === 'lens' ? 1 : bandAlpha(height, BANDS.resourceChip);
}

/** The band resource props use. */
export function resourcePropAlpha(height: number, view: ResourceView): number {
  return bandAlpha(height, view === 'lens' ? BANDS.lensProp : BANDS.resourceProp);
}

// ─── Props ─────────────────────────────────────────────────────────────────────────────────────

/** Instanced resource props (asset ids: industry.pumpjack, .derrick, .offshore, .mine). */
export type PropKind = 'pumpjack' | 'derrick' | 'offshore' | 'mine';

/** Which prop marks a resource; oil and gas on the sea use the platform, minerals never stand at sea. */
export function propKindOf(kind: number, sea: boolean): PropKind | null {
  if (kind === RESOURCE.OIL || kind === RESOURCE.GAS) {
    if (sea) {
      return 'offshore';
    }
    return kind === RESOURCE.OIL ? 'pumpjack' : 'derrick';
  }
  return sea ? null : 'mine';
}

export interface Spot {
  /** Offset from the hex centre, world units (east, south). */
  x: number;
  z: number;
  yaw: number;
}

/**
 * `n` spots evenly spread on a ring, rotated by `spin` (0..1 of a turn, from a hash of the hex so
 * neighbouring hexes do not line up). n = 1 puts the prop on the ring, not the centre.
 */
export function ringSpots(n: number, spin: number, radius: number): Spot[] {
  const spots: Spot[] = [];
  for (let i = 0; i < n; i++) {
    const a = (spin + i / n) * Math.PI * 2;
    spots.push({ x: Math.cos(a) * radius, z: Math.sin(a) * radius, yaw: -a + Math.PI / 2 });
  }
  return spots;
}

// ─── Models ────────────────────────────────────────────────────────────────────────────────────

/** Drawn silhouettes, chosen for the eye only: no record says a site is a castle or a temple. */
export type Landmark = 'castle' | 'dome' | 'temple' | 'church';

const CULTURAL_LANDMARKS: readonly Landmark[] = ['castle', 'dome', 'temple'];
const HASH_SHIFT = 7; // FNV's lowest bits are the weakest

/** The model of a site: none for a natural site (its chip alone marks it), a spire for a mixed one. */
export function landmarkOf(site: Pick<WonderSite, 'category' | 'rank'>): Landmark | null {
  if (site.category === 'natural') {
    return null;
  }
  if (site.category === 'mixed') {
    return 'church';
  }
  return CULTURAL_LANDMARKS[(site.rank >>> HASH_SHIFT) % CULTURAL_LANDMARKS.length];
}

/** Danger sites first, then the first inscribed of a country, then the neutral hash. */
export function byPriority(a: WonderSite, b: WonderSite): number {
  const tier = (s: WonderSite): number => (s.danger ? 0 : s.lead ? 1 : 2);
  return tier(a) - tier(b) || a.rank - b.rank || a.hex - b.hex;
}

/** A model keeps this many hexes clear round it (2 = a 5x5 hex neighbourhood). */
export const MODEL_CLEAR_HEXES = 2;

/**
 * The sites that get a model: best first, each at least MODEL_CLEAR_HEXES + 1 hexes from any kept
 * one, so a crowded region shows a few monuments, not a carpet. `canStand` says where a model fits.
 */
export function pickModels(sites: readonly WonderSite[], canStand: (site: WonderSite) => boolean): WonderSite[] {
  const kept: WonderSite[] = [];
  const byRow = new Map<number, WonderSite[]>();
  for (const site of [...sites].sort(byPriority)) {
    if (!canStand(site)) {
      continue;
    }

    const row = hexRow(site.hex);
    let clear = true;
    for (let r = row - MODEL_CLEAR_HEXES; r <= row + MODEL_CLEAR_HEXES && clear; r++) {
      clear = !(byRow.get(r) ?? []).some((other) => hexDistance(other.hex, site.hex) <= MODEL_CLEAR_HEXES);
    }
    if (!clear) {
      continue;
    }

    kept.push(site);
    const list = byRow.get(row);
    if (list) {
      list.push(site);
      continue;
    }
    byRow.set(row, [site]);
  }
  return kept;
}
